import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { injectApiUrl } from '@seatix/shared-config';
import { Observable } from 'rxjs';
import { withExpectedUnauthorized } from '../auth-interceptor/auth-context';
import { ApiResponse } from '@seatix/shared-domain-utils';
import {
  AppleCallbackCredentials,
  ForgotPasswordResponse,
  LoginCredentials,
  LoginResponse,
  PhoneVerificationInitiateRequest,
  PhoneVerificationInitiateResponse,
  PhoneVerificationVerifyRequest,
  PhoneVerificationVerifyResponse,
  RegisterCredentials,
  RegisterResponse,
  ResetPasswordResponse,
  SocialAuthResponse,
  User,
  ValidateTokenResponse,
  WhatsAppCompleteRegistrationCredentials,
  WhatsAppInitiateCredentials,
  WhatsAppOtpResponse,
  WhatsAppVerifyCredentials,
  WhatsAppVerifyResponse,
} from '@seatix/shared-domain-auth';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  readonly apiUrl = injectApiUrl();
  readonly http = inject(HttpClient);

  login(credentials: LoginCredentials, turnstileToken: string) {
    return this.http.post<LoginResponse>(
      `${this.apiUrl}/api/auth/login/session`,
      {
        ...credentials,
        turnstile_token: turnstileToken,
      },
      { context: withExpectedUnauthorized() },
    );
  }

  getUserData(context?: HttpContext): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/api/auth/me`, {
      context,
    });
  }

  register(credentials: RegisterCredentials, turnstileToken: string) {
    return this.http.post<RegisterResponse>(
      `${this.apiUrl}/api/auth/signup`,
      {
        ...credentials,
        turnstile_token: turnstileToken,
      },
      {
        params: { mode: 'session' },
      },
    );
  }

  logout(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/api/auth/me/logout/session`,
      {},
    );
  }

  handleGoogleCallback(token: string, profile = false) {
    return this.http.post<SocialAuthResponse>(
      `${this.apiUrl}/api/auth/social/google/callback`,
      { token },
      profile ? { params: { profile: '1' } } : undefined,
    );
  }

  handleAppleCallback(credentials: AppleCallbackCredentials) {
    return this.http.post<SocialAuthResponse>(
      `${this.apiUrl}/api/auth/social/apple/callback`,
      credentials,
    );
  }

  verifyEmail(code: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/api/auth/verify`,
      { code },
    );
  }

  resendVerification() {
    return this.http.post<ApiResponse<null>>(
      `${this.apiUrl}/api/auth/send-verification`,
      {},
    );
  }

  initiateWhatsAppOtp(
    credentials: WhatsAppInitiateCredentials,
    turnstileToken: string,
  ): Observable<WhatsAppOtpResponse> {
    return this.http.post<WhatsAppOtpResponse>(
      `${this.apiUrl}/api/auth/whatsapp/initiate`,
      {
        ...credentials,
        turnstile_token: turnstileToken,
      },
    );
  }

  verifyWhatsAppOtp(
    credentials: WhatsAppVerifyCredentials,
  ): Observable<WhatsAppVerifyResponse> {
    return this.http.post<WhatsAppVerifyResponse>(
      `${this.apiUrl}/api/auth/whatsapp/verify`,
      credentials,
    );
  }

  completeWhatsAppRegistration(
    credentials: WhatsAppCompleteRegistrationCredentials,
  ): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(
      `${this.apiUrl}/api/auth/whatsapp/complete-registration`,
      credentials,
    );
  }

  patchUser(user: Partial<User>) {
    return this.http.patch<ApiResponse<Partial<User>>>(
      `${this.apiUrl}/api/auth/me`,
      user,
    );
  }

  // Las dos rutas `/me/phone/*` van por Sanctum y no pasan por
  // `ClientValidationMiddleware`: la sesión ya identifica al llamante, así que
  // no llevan `turnstile_token` — mandarlo sería ruido que el backend ignora y
  // una pista falsa de que este flujo necesita bot-check.
  initiatePhoneVerification(request: PhoneVerificationInitiateRequest) {
    return this.http.post<PhoneVerificationInitiateResponse>(
      `${this.apiUrl}/api/auth/me/phone/initiate`,
      request,
    );
  }

  verifyPhoneOwnership(request: PhoneVerificationVerifyRequest) {
    return this.http.post<PhoneVerificationVerifyResponse>(
      `${this.apiUrl}/api/auth/me/phone/verify`,
      request,
    );
  }

  requestPasswordReset(email: string, turnstile_token: string) {
    return this.http.post<ForgotPasswordResponse>(
      `${this.apiUrl}/api/auth/password/forgot`,
      { email, turnstile_token },
    );
  }

  validateResetToken(token: string, email: string) {
    return this.http.get<ValidateTokenResponse>(
      `${this.apiUrl}/api/auth/password/reset/validate`,
      { params: { token, email } },
    );
  }

  resetPassword(
    credentials: {
      email: string;
      token: string;
      password: string;
      password_confirmation: string;
    },
    turnstileToken: string,
  ) {
    return this.http.post<ResetPasswordResponse>(
      `${this.apiUrl}/api/auth/password/reset`,
      {
        email: credentials.email,
        token: credentials.token,
        password: credentials.password,
        password_confirmation: credentials.password_confirmation,
        turnstile_token: turnstileToken,
      },
    );
  }
}
